// NutritionLogModal — the confirm/review modal for Mirror-driven nutrition
// capture. Two entry points feed the SAME modal:
//   - voice: propose_nutrition_log -> nutrition-extract -> prefill (source 'voice')
//   - photo: camera attach -> nutrition-photo-analyze -> prefill (source 'photo')
//
// Mirrors the agenda mold (CreateEventModal): reuses the exported
// AgendaBottomSheet wrapper for identical premium styling, hydrates editable
// fields from a prefill, writes via the existing useNutrition.logMeal (source +
// confidence + ingredients + client_action_id idempotency), then emits a
// capture recap so the Espelho shows "what I just saved".
//
// Perf: the heavy useNutrition hook (it fetches the day's logs on mount) is
// DEFERRED — the outer component returns null until `open`, so nothing runs on
// every Mirror render even though useModuleCapture keeps this in the tree.

import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageProvider";
import { useNutrition, type MealType } from "@/hooks/useNutrition";
import { AgendaBottomSheet } from "@/components/agenda/CreateEventModal";
import { emitCaptureRecap } from "@/lib/capture/captureRecapBus";
import { sfx } from "@/lib/sfx";
import ReportContentButton from "@/components/report/ReportContentButton";
import { REPORT_SURFACES, nutritionPhotoContentId } from "@/lib/report/surfaces";

export interface NutritionPrefill {
  description: string;
  meal_type: MealType;
  kcal: number;
  protein: number;
  carbs: number;
  fat: number;
  confidence?: number | null;
  ingredients_detected?: string[] | null;
  /** 'voice' for spoken capture, 'photo' for the camera attach path. */
  source: "voice" | "photo";
  /** Per-turn idempotency key — "mirror_msg_<action_id>" / "mirror_photo_<id>". */
  client_action_id?: string;
  /** Optional meal photo (data URL) shown as a thumbnail on the photo path. */
  imageDataUrl?: string | null;
  /** Optional raw phrase for the voice path "you said" reference. */
  transcriptRaw?: string | null;
}

interface NutritionLogModalProps {
  open: boolean;
  prefill: NutritionPrefill | null;
  onClose: () => void;
  onCreated: () => void;
}

const MEAL_TYPE_OPTIONS: { value: MealType; labelKey: string }[] = [
  { value: "breakfast", labelKey: "nutrition.breakfast" },
  { value: "lunch", labelKey: "nutrition.lunch" },
  { value: "snack", labelKey: "nutrition.snack" },
  { value: "dinner", labelKey: "nutrition.dinner" },
  { value: "pre_workout", labelKey: "nutrition.preWorkout" },
  { value: "post_workout", labelKey: "nutrition.postWorkout" },
];

/**
 * Outer shell — defers the useNutrition hook (and its day-log fetch) until the
 * modal is actually open. Trades the bottom-sheet exit animation for not
 * running a data fetch on every Mirror hub render.
 */
export default function NutritionLogModal({ open, prefill, onClose, onCreated }: NutritionLogModalProps) {
  if (!open || !prefill) return null;
  return <NutritionLogModalInner prefill={prefill} onClose={onClose} onCreated={onCreated} />;
}

function NutritionLogModalInner({
  prefill,
  onClose,
  onCreated,
}: {
  prefill: NutritionPrefill;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useLanguage();
  const { logMeal, userId } = useNutrition();
  const saveLock = useRef(false);
  const formOwner = useRef<string | null>(null);
  useEffect(() => {
    if (formOwner.current && formOwner.current !== userId) { onClose(); return; }
    if (userId) formOwner.current = userId;
  }, [userId, onClose]);

  const [description, setDescription] = useState(prefill.description ?? "");
  const [mealType, setMealType] = useState<MealType>(prefill.meal_type ?? "snack");
  const [kcal, setKcal] = useState(prefill.kcal ?? 0);
  const [protein, setProtein] = useState(prefill.protein ?? 0);
  const [carbs, setCarbs] = useState(prefill.carbs ?? 0);
  const [fat, setFat] = useState(prefill.fat ?? 0);
  const [saving, setSaving] = useState(false);

  const ingredients = prefill.ingredients_detected ?? [];

  const confidenceBadge = (() => {
    const c = prefill.confidence;
    if (typeof c !== "number") return null;
    if (c >= 0.85) return { text: t("nutrition.photo.confidence_high"), color: "hsl(140 60% 50%)" };
    if (c >= 0.6) return { text: t("nutrition.photo.confidence_medium"), color: "hsl(38 92% 55%)" };
    return { text: t("nutrition.photo.confidence_low"), color: "hsl(0 72% 60%)" };
  })();

  const handleSave = async () => {
    if (saveLock.current) return;
    if (!description.trim()) {
      toast.error(t("nutrition.photo.error"));
      return;
    }
    saveLock.current = true; setSaving(true);
    try {
      await logMeal({
        meal_type: mealType,
        description: description.trim(),
        kcal,
        protein,
        carbs,
        fat,
        source: prefill.source,
        confidence: typeof prefill.confidence === "number" ? prefill.confidence : undefined,
        ingredients_detected: ingredients.length > 0 ? ingredients : null,
        client_action_id: prefill.client_action_id,
      });
      sfx.confirm("module");
      const mealLabel = t(
        MEAL_TYPE_OPTIONS.find((m) => m.value === mealType)?.labelKey ?? "nutrition.snack",
      );
      emitCaptureRecap({
        kind: "nutrition",
        title: t("mirror.recap.nutrition"),
        subtitle: mealLabel + " · " + description.trim() + " (" + kcal + " kcal)",
      });
      onCreated();
      onClose();
    } catch (e) {
      console.error("[NutritionLogModal] save failed:", e);
      sfx.error("module");
      toast.error(t("nutrition.photo.error"));
    } finally {
      saveLock.current = false; setSaving(false);
    }
  };

  const inputStyle = {
    background: "hsl(var(--card))",
    border: "1px solid hsl(187 72% 50% / 0.15)",
  };
  const labelStyle = { color: "hsl(187 72% 50% / 0.55)" };

  const footer = (
    <button
      onClick={handleSave}
      disabled={saving || !userId}
      className="w-full py-3.5 rounded-xl font-display text-[14px] font-semibold tracking-wide transition-all duration-200 active:scale-[0.98]"
      style={{
        background: saving ? "hsl(187 72% 50% / 0.25)" : "hsl(187 72% 50%)",
        color: "hsl(var(--background))",
      }}
    >
      {saving ? t("agenda.saving") : t("nutrition.photo.save")}
    </button>
  );

  return (
    <AgendaBottomSheet open dismissDisabled={saving} onClose={() => { if (!saveLock.current) onClose(); }} title={t("nutrition.log.title")} footer={footer}>
      {/* Photo thumbnail (photo path only) */}
      {prefill.imageDataUrl && (
        <img
          src={prefill.imageDataUrl}
          alt=""
          className="w-full max-h-[26vh] rounded-2xl object-cover mb-4"
        />
      )}

      {/* "You said" reference (voice path) */}
      {!prefill.imageDataUrl && prefill.transcriptRaw && (
        <div
          className="mb-4 rounded-xl px-3 py-2.5"
          style={{ background: "hsl(187 72% 50% / 0.06)", border: "1px solid hsl(187 72% 50% / 0.18)" }}
        >
          <span
            className="text-[9px] font-display tracking-[0.18em] uppercase mb-1 block"
            style={{ color: "hsl(187 72% 60% / 0.75)" }}
          >
            {t("agenda.voice.youSaid")}
          </span>
          <p className="text-[12px] font-display text-muted-foreground italic leading-snug">
            &ldquo;{prefill.transcriptRaw}&rdquo;
          </p>
        </div>
      )}

      {/* Denuncia + selo de confianca na mesma linha. So no caminho da FOTO:
          ali o modelo ESCREVE uma descricao do que acha que esta no prato — e
          conteudo gerado. No caminho da voz ele so estrutura a fala do proprio
          usuario, que nao e o modelo falando.
          O corte desta superficie e DESCARTAR, nao esconder: nada foi gravado
          ainda, entao a denuncia fecha o modal e a analise morre aqui. */}
      {(prefill.source === "photo" || confidenceBadge) && (
        <div className="mb-3 flex justify-end items-center gap-1">
          {confidenceBadge && (
            <span
              className="px-3 py-1 rounded-full font-display text-[10px] font-semibold tracking-wide"
              style={{
                background: confidenceBadge.color + "22",
                color: confidenceBadge.color,
                border: "1px solid " + confidenceBadge.color + "55",
              }}
            >
              {confidenceBadge.text}
            </span>
          )}
          {prefill.source === "photo" && prefill.description && (
            <ReportContentButton
              surface={REPORT_SURFACES.NUTRITION_PHOTO}
              contentId={nutritionPhotoContentId(prefill.description)}
              snapshot={[
                prefill.description,
                ingredients.length > 0 ? ingredients.join(", ") : "",
              ].filter(Boolean).join("\n\n")}
              context={{
                module: "nutrition",
                source: "photo",
                confidence: prefill.confidence ?? null,
                meal_type: prefill.meal_type,
              }}
              onReported={onClose}
            />
          )}
        </div>
      )}

      {/* Description */}
      <label className="block mb-4">
        <span className="text-[10px] font-display tracking-[0.16em] uppercase mb-1.5 block" style={labelStyle}>
          {t("nutrition.log.description")}
        </span>
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full rounded-xl px-4 py-3 text-[14px] font-display text-foreground placeholder:text-muted-foreground outline-none"
          style={inputStyle}
        />
      </label>

      {/* Meal type */}
      <label className="block mb-4">
        <span className="text-[10px] font-display tracking-[0.16em] uppercase mb-1.5 block" style={labelStyle}>
          {t("nutrition.log.mealType")}
        </span>
        <select
          value={mealType}
          onChange={(e) => setMealType(e.target.value as MealType)}
          className="w-full rounded-xl px-3 py-2.5 text-[13px] font-display text-foreground outline-none"
          style={{ ...inputStyle, colorScheme: "dark" }}
        >
          {MEAL_TYPE_OPTIONS.map((m) => (
            <option key={m.value} value={m.value}>
              {t(m.labelKey)}
            </option>
          ))}
        </select>
      </label>

      {/* Macros */}
      <div className="grid grid-cols-2 gap-2 mb-4">
        <NumField label="kcal" value={kcal} onChange={setKcal} />
        <NumField label="P (g)" value={protein} onChange={setProtein} />
        <NumField label="C (g)" value={carbs} onChange={setCarbs} />
        <NumField label="F (g)" value={fat} onChange={setFat} />
      </div>

      {/* Detected ingredients */}
      {ingredients.length > 0 && (
        <div className="mb-2">
          <p
            className="text-[10px] font-display tracking-[0.18em] uppercase mb-2"
            style={{ color: "hsl(187 72% 60% / 0.6)" }}
          >
            {t("nutrition.photo.ingredients_label")}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {ingredients.map((ing, i) => (
              <span
                key={i}
                className="px-2.5 py-1 rounded-full text-[11px] font-body"
                style={{
                  background: "hsl(187 72% 50% / 0.10)",
                  color: "hsl(187 72% 70% / 0.85)",
                  border: "1px solid hsl(187 72% 50% / 0.22)",
                }}
              >
                {ing}
              </span>
            ))}
          </div>
        </div>
      )}
    </AgendaBottomSheet>
  );
}

function NumField({ label, value, onChange }: { label: string; value: number; onChange: (n: number) => void }) {
  return (
    <div>
      <p className="text-[10px] font-display tracking-wide mb-1" style={{ color: "hsl(218 14% 50%)" }}>{label}</p>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(Math.max(0, Number(e.target.value) || 0))}
        className="w-full rounded-xl px-3 py-2 text-[14px] font-display font-semibold text-foreground outline-none"
        style={{ background: "hsl(var(--card))", border: "1px solid hsl(187 72% 50% / 0.15)" }}
      />
    </div>
  );
}
